import { useEffect, useRef } from 'react';
import useScrollY from '../hooks/useScrollProgress.js';

const SHAPES = [
  { className: 'top-[18%] left-[8%] h-24 w-24 rounded-2xl', anim: 'animate-float-slow', grad: 'from-cyan/40 to-transparent', blur: 'blur-[2px]' },
  { className: 'top-[60%] left-[18%] h-16 w-16 rounded-full', anim: 'animate-float-med', grad: 'from-cyan-glow/30 to-transparent', blur: '' },
  { className: 'top-[28%] right-[12%] h-32 w-32 rounded-[2rem]', anim: 'animate-float-fast', grad: 'from-silver/20 to-transparent', blur: 'blur-[1px]' },
  { className: 'bottom-[18%] right-[20%] h-20 w-20 rotate-12 rounded-xl', anim: 'animate-float-slow', grad: 'from-cyan/30 to-transparent', blur: '' },
  { className: 'top-[42%] right-[28%] h-10 w-10 rounded-full', anim: 'animate-float-med', grad: 'from-cyan-glow/50 to-transparent', blur: '' },
  { className: 'bottom-[28%] left-[10%] h-14 w-14 rotate-45', anim: 'animate-float-fast', grad: 'from-silver/25 to-transparent', blur: 'blur-[2px]' },
];

export default function Hero() {
  const scrollY = useScrollY();
  const heroRef = useRef(null);

  // Parallax: translate background layers at different rates
  const bgY = scrollY * 0.35;
  const midY = scrollY * 0.18;
  const contentY = scrollY * 0.08;
  const fade = Math.max(0, 1 - scrollY / 600);

  useEffect(() => {
    document.body.style.setProperty('--hero-fade', fade);
  }, [fade]);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-hero-gradient bg-[length:200%_200%] animate-gradient-drift"
    >
      {/* Parallax background layers */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ transform: `translate3d(0, ${bgY}px, 0)` }}
      >
        {/* Grid mesh */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,207,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,207,255,0.4) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
          }}
        />
        {/* Glow blobs */}
        <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-cyan/20 blur-[120px]" />
        <div className="absolute right-0 top-0 h-[28rem] w-[28rem] rounded-full bg-navy-500/40 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-cyan-glow/10 blur-[120px]" />
      </div>

      {/* Floating geometric shapes */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ transform: `translate3d(0, ${midY}px, 0)` }}
      >
        {SHAPES.map((s, i) => (
          <div
            key={i}
            className={`absolute ${s.className} ${s.anim} ${s.blur} bg-gradient-to-br ${s.grad} border border-white/10 shadow-glow`}
          />
        ))}
      </div>

      {/* Content */}
      <div
        className="container-x relative z-10 pt-32 pb-28"
        style={{ transform: `translate3d(0, ${contentY}px, 0)`, opacity: fade }}
      >
        <div className="max-w-4xl">
          <div className="reveal is-visible flex items-center gap-3 mb-8">
            <span className="eyebrow">Software Studio · Bangkok</span>
          </div>

          <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl xl:text-8xl">
            <span className="heading-gradient">Empowering Your</span>
            <br />
            <span className="bg-clip-text text-transparent bg-cyan-sheen text-shadow-glow">
              Business
            </span>
            <span className="heading-gradient"> with Software</span>
          </h1>

          <p className="mt-8 max-w-2xl text-lg text-silver/85 sm:text-xl">
            Custom solutions, SaaS integration, and IT consulting tailored to your needs.
            We engineer software that compounds in value — scalable, secure, and built to last.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#contact" className="btn-primary animate-pulse-glow text-base">
              Start Your Project
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="#portfolio" className="btn-ghost text-base">
              See Our Work
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-16 grid max-w-2xl grid-cols-2 gap-6 sm:grid-cols-4">
            {[
              { v: '120+', l: 'Projects shipped' },
              { v: '14', l: 'Industries' },
              { v: '99.98%', l: 'Avg uptime' },
              { v: '12 yrs', l: 'In business' },
            ].map((s) => (
              <div key={s.l} className="border-l border-cyan/30 pl-4">
                <div className="font-heading text-2xl font-bold text-white sm:text-3xl">{s.v}</div>
                <div className="text-xs text-silver/70 sm:text-sm">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#services"
        aria-label="Scroll to services"
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-silver/60 transition-colors hover:text-cyan md:flex"
        style={{ opacity: fade }}
      >
        <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
        <span className="flex h-10 w-6 justify-center rounded-full border border-silver/30 p-1">
          <span className="h-2 w-1 animate-float-med rounded-full bg-cyan" />
        </span>
      </a>

      {/* Bottom fade into next section */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy-900 to-transparent" />
    </section>
  );
}
